import { programLanguagesList } from "config/optionValues";

type PresignedUrlSignalType = "create" | "update" | "delete";

export interface MyLearningJourneyQuiz {
  _id: string;
  description: string;
  title: string;
}

export interface QuizQuestionOptionType {
  answer: string;
  correct: boolean;
}

export const QuizQuestionOptionDefault: QuizQuestionOptionType = {
  answer: "",
  correct: false,
};

export interface QuizQuestionType {
  question: string;
  type: string;
  answer: string;
  options: QuizQuestionOptionType[];
  noCorrectOptionsError: string;
  duplicateOptionsError: string;
}

export const QuizQuestionDefault: QuizQuestionType = {
  question: "",
  type: "",
  answer: "",
  options: Array(4).fill(QuizQuestionOptionDefault),
  noCorrectOptionsError: "",
  duplicateOptionsError: "",
};

export interface QuizType {
  _id?: string;
  title: string;
  course?: Course | string;
  questions: QuizQuestionType[];
  randomizeQuestionOrder: boolean;
  randomizeOptionsOrder: boolean;
  canEdit?: boolean;
}

export const QuizDefault: QuizType = {
  _id: "",
  title: "",
  questions: [],
  randomizeQuestionOrder: false,
  randomizeOptionsOrder: false,
};

export interface CodeFile {
  name: string;
  content: string;
}

export interface Program {
  _id: string;
  title: string;
  description: string;
  headerImage: string;
  badgeImage: string;
  aptitude: string;
  level: string;
  age: string;
  experiencePoints: number;
  courseList: string[];
  planDocument?: string;
  courses: Course[];
  projects: Project[];
  modules: Module[];
  classes: ClassListType[];
  organisations?: SchoolListType[] | string[];
  canEdit: boolean;
  profanityFlag?: boolean;
}

export interface Course {
  _id: string;
  title: string;
  description: string;
  headerImage: string;
  badgeImage: string;
  url: string;
  experiencePoints: number;
  level: string;
  planDocument?: string;
  programs: Program[];
  projects: Project[] | string[];
  modules: Module[];
  quizzes: QuizListType[];
  classes: ClassListType[];
  organisations?: SchoolListType[] | string[];
  canEdit: boolean;
  profanityFlag?: boolean;
}

export const CourseDefault = {
  _id: "",
  title: "",
  description: "",
  url: "",
  experiencePoints: 0,
  level: "",
  headerImage: "",
  badgeImage: "",
  programs: [],
  projects: [],
  modules: [],
  quizzes: [],
  classes: [],
};

export interface Project {
  _id: string;
  title: string;
  description: string;
  headerImage: string;
  badgeImage: string;
  level: string;
  experiencePoints: number;
  estimatedTime: number;
  videoUrl: string;
  videoTranscript: string;
  videoHash: string;
  planDocument: string;
  planName: string;
  pdfPlanText: string;
  initialCodeName: string;
  initialCode: string;
  finalCodeName: string;
  finalCode: string;
  language: string;
  programId: Program[];
  courseId: Course[];
  modules: Module[];
  classes: ClassListType[];
  organisations?: SchoolListType[] | string[];
  canEdit: boolean;
  profanityFlag?: boolean;
  context?: {
    topicArea: string | undefined;
    BANNED_TOPICS: string[] | undefined;
  }
}

export const ProjectDefault = {
  _id: "",
  title: "",
  description: "",
  headerImage: "",
  badgeImage: "",
  level: "",
  experiencePoints: 0,
  estimatedTime: 0,
  planDocument: "",
  planName: "",
  pdfPlanText: "",
  finalCode: "",
  videoUrl: "",
  videoTranscript: "",
  videoHash: "",
  language: "",
  programId: [],
  courseId: [],
  modules: [],
  classes: [],
};

export interface Module {
  _id: string;
  title: string;
  description: string;
  headerImage: string;
  badgeImage: string;
  level: string;
  language: string;
  experiencePoints: number;
  estimatedTime: number;
  planDocument: string;
  planName: string;
  videoUrl: string;
  contentUrl: string;
  initialCodeName: string;
  initialCode: string;
  finalCodeName: string;
  finalCode: string;
  programId: Program[];
  courseId: Course[];
  projectId: Project[];
  classes: ClassListType[];
  organisations?: SchoolListType[] | string[];
  canEdit: boolean;
  profanityFlag?: boolean;
  context?: {
    topicArea: string | undefined;
    BANNED_TOPICS: string[] | undefined;
  }
}

export const ModuleDefault = {
  _id: "",
  title: "",
  description: "",
  headerImage: "",
  badgeImage: "",
  level: "",
  language: "",
  experiencePoints: 0,
  estimatedTime: 0,
  planDocument: "",
  planName: "",
  videoUrl: "",
  contentUrl: "",
  programId: [],
  courseId: [],
  projectId: [],
  classes: [],
};

export interface tempCourseType {
  _id: string;
  title: string;
  description: string;
  aptitude: string;
  classes: string[];
}

export interface QuizListType {
  _id: string;
  program: string;
  title: string;
  description: string;
  questions: QuestionType[];
  course: Course | string;
  level: string;
  points: number;
  estTime: number;
  active: boolean;
  tempId?: string;
  badgeImage?: string;
  badgeImageSignal?: PresignedUrlSignalType;
}

export interface ClassListType {
  _id: string;
  name: string;
  teachers: ContactListType[];
  numBadges?: number;
  licenses?: number;
  enrolmentRate?: number;
  progressRate?: number;
  aptitudeRate?: number;
  programs?: string[];
  courses?: string[];
  projects?: string[];
  modules?: string[];
  students?: StudentListType[];
  active: boolean;
  organisations?: string[];
  tempId?: string;
  leaderboard?: {
    name: string;
    activate: boolean;
  };
  canEdit?: boolean;
}

export type ClassImportListType = string;

export interface ContactListType {
  _id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  phone: string;
  email: string;
  role?: string;
  lastActiveAt?: Date;
  classes?: ClassListType[] | string[];
  tempId?: string;
  canEdit?: boolean;
  canDelete?: boolean;
}

export type CreateEditContactType =
  | {
      firstName: string;
      middleName: string;
      lastName: string;
      email: string;
      phone: string;
      organisations?: string[];
    }
  | { [key: string]: any };

export interface TeacherListType extends ContactListType {
  classes: ClassListType[];
}

export interface ParentListType extends ContactListType {
  students: StudentListType[];
}

export interface StudentListType {
  _id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  heritage: boolean;
  classes: ClassListType[];
  parents: ParentListType[];
  aptitudeRate?: number;
  progressRate?: number;
  averageScore?: number;
  badges?: [];
  status?: string;
  username?: string;
  lastActiveAt?: Date;
  role?: string;
  canEdit?: boolean;
  canDelete?: boolean;
}

export interface StudentImportListType {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  heritage?: boolean;
  username: string;
  classes: string[];
  notes?: string[];
}

export interface StudentResultsType extends StudentListType {
  // courseCompleted: number;
  // avgQuizScore: number;
  aptitude: number;
  progress: number;
}

export interface SchoolListType {
  _id: string;
  logo?: string;
  name: string;
  contacts: ContactListType[];
  admins: ContactListType[];
  students?: StudentListType[];
  licenses: number;
  locations: LocationListType[];
  classes: ClassListType[];
}

export interface ResponseOption {
  text: string;
  correct: boolean;
}

export interface QuestionType {
  title: string;
  responses: ResponseOption[];
}

export interface LocationListType {
  address: string;
  city: string;
  state: string;
  postCode: string;
}

export interface LessonListType {
  _id?: string;
  title: string;
  headerImage?: string | null;
  badgeImage?: string | null;
  description: string;
  level?: string;
  experiencePoints?: number;
  planDocument?: string | null;
  planName?: string;
  videoUrl?: string;
  language?: string;
  quizzes?: string[];
  initialCode?: string | null;
  finalCode?: string | null;
  initialCodeName?: string | null;
  finalCodeName?: string | null;
  organisations?: string[];
  context?: {
    topicArea: string | undefined;
    BANNED_TOPICS: string[] | undefined;
  }
}

export interface BrandListType {
  _id: string;
  logo?: string;
  title: string;
  host: string;
}

export interface ScratchInstructions {
  header: string;
  paragraph: string;
}

export type AssistantButtonType = {
  "I want some feedback": string;
  "I'm stuck, Can you help me?": string;
  "I don't understand the logic": string;
  // "How can I run the code?": string;
  "I can see an error": string;
};

export type AssistantButtonKeys = keyof AssistantButtonType;
export type AssistantAllButtonTypes =
  | AssistantButtonKeys
  | "Please check my code"
  | "chat"
  | string
  | undefined;

export const lessonType = (lesson: any): string | undefined => {
  return isProgram(lesson)
    ? "programs"
    : isCourse(lesson)
      ? "courses"
      : isProject(lesson)
        ? "projects"
        : isModule(lesson)
          ? "modules"
          : undefined;
};

const hasAllProperties = (obj: any, props: string[]) => {
  for (const prop of props) {
    if (!obj.hasOwnProperty(prop)) return false;
  }
  return true;
};

export type ArrayElement<T> = T extends (infer U)[] ? U : never;

// Define a language type based on the list of possible program languages
export type languageType = (typeof programLanguagesList)[number];

const isProgram = (lesson: any): lesson is Program =>
  lesson && hasAllProperties(lesson, ["courses", "projects", "modules"]);
const isCourse = (lesson: any): lesson is Course =>
  lesson && hasAllProperties(lesson, ["programs", "projects", "modules"]);
const isProject = (lesson: any): lesson is Project =>
  lesson && hasAllProperties(lesson, ["programs", "courses", "modules"]);
const isModule = (lesson: any): lesson is Module =>
  lesson && hasAllProperties(lesson, ["programs", "courses", "projects"]);

export const defaultContact = {
  _id: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

export interface Message {
  content: string;
  role: "user" | "assistant";
  complete?: true;
}

export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
}

export type FetchPresignedUrlImageType = "loading" | "not-found" | "successful";
